import * as React from "react";
import { Spinner } from "@fluentui/react-components";
import { Mail20Regular } from "@fluentui/react-icons";
import { graphMailService, isMailUnavailable, type EntwurfDetail } from "../../services/graphMailService";
import type { OpenWindowConfig } from "../windowManager/WindowManagerContext";
import { ComposeMailContent } from "./ComposeMailWindow";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { useResponsive } from "../../hooks/useResponsive";
import { meldeEntwurfGeaendert } from "./entwurfBus";

/**
 * T-212 — einen gespeicherten Entwurf im Mailfenster weiterschreiben.
 *
 * Lädt den Entwurf (Felder, Text mit Inline-Bildern, Datei-Anhänge, KMVS-
 * Kontext) und öffnet dann das normale Verfassen-Fenster damit. Der Editor
 * übernimmt seinen Anfangsinhalt nur beim ersten Rendern — deshalb wird er
 * erst gezeigt, wenn der Entwurf da ist.
 */
export const EntwurfFenster: React.FC<{
    windowId: string;
    mailbox: string;
    draftId: string;
    /** Nach erfolgreichem Senden — z. B. der Vorgang lädt seinen Verlauf neu. */
    onSent?: () => void | Promise<void>;
}> = ({ windowId, mailbox, draftId, onSent }) => {
    const [detail, setDetail] = React.useState<EntwurfDetail | null>(null);
    const [fehler, setFehler] = React.useState<string | null>(null);
    // Handy: das Fenster hat dort keine Titelleiste — solange der Editor (mit
    // eigenem X) noch nicht steht, braucht es hier einen Weg hinaus.
    const { closeWindow } = useWindowManager();
    const { isPhone } = useResponsive();
    const schliessen = isPhone ? (
        <button
            type="button"
            onClick={() => closeWindow(windowId)}
            style={{ margin: "calc(12px + env(safe-area-inset-top)) 12px 0", alignSelf: "flex-start", minHeight: 44, padding: "0 16px", borderRadius: 999, border: "1px solid #e6dbe4", background: "#fff", fontSize: 15, fontWeight: 600, color: "#4d2051" }}
        >
            Schließen
        </button>
    ) : null;

    React.useEffect(() => {
        let aktiv = true;
        graphMailService
            .getDraft(mailbox, draftId)
            .then((d) => {
                if (aktiv) setDetail(d);
            })
            .catch((err: unknown) => {
                if (!aktiv) return;
                const weg = isMailUnavailable(err);
                setFehler(
                    weg
                        ? "Der Entwurf wurde nicht gefunden. Vielleicht wurde er inzwischen gesendet oder gelöscht."
                        : "Der Entwurf konnte nicht geladen werden. Bitte später erneut versuchen.",
                );
                // Die Karte, über die er geöffnet wurde, soll nicht stehen bleiben
                // (Befund 03.11.2026: in Outlook gesendet) — Vorgang und
                // Posteingang laden ihre Entwürfe neu.
                if (weg) meldeEntwurfGeaendert(mailbox);
            });
        return () => {
            aktiv = false;
        };
    }, [mailbox, draftId]);

    if (fehler) {
        return (
            <div style={{ display: "flex", flexDirection: "column" }}>
                {schliessen}
                <div role="alert" style={{ padding: 24, color: "#a4262c", fontSize: 14 }}>
                    {fehler}
                </div>
            </div>
        );
    }
    if (!detail) {
        return (
            <div style={{ display: "flex", flexDirection: "column" }}>
                {schliessen}
                <div style={{ padding: 32, display: "flex", justifyContent: "center" }}>
                    <Spinner size="small" label="Entwurf wird geladen …" />
                </div>
            </div>
        );
    }
    const k = detail.kontext;
    return (
        <ComposeMailContent
            windowId={windowId}
            mailbox={detail.mailbox}
            to={detail.to}
            cc={detail.cc}
            bcc={detail.bcc}
            subject={detail.subject}
            body={detail.bodyHtml}
            mode={k?.mode && k.mode !== "new" ? k.mode : undefined}
            replyToMessageId={k?.replyToMessageId ?? undefined}
            replySourceMailbox={k?.replySourceMailbox ?? undefined}
            vorgangId={k?.vorgangId ?? undefined}
            todoId={k?.todoId ?? undefined}
            inheritLinksFromMessageId={k?.inheritLinksFromMessageId ?? undefined}
            contactId={k?.contactId ?? null}
            entwurf={detail}
            {...(onSent ? { onSent } : {})}
        />
    );
};

/** Öffnet einen Entwurf im Mailfenster. Ein schon offenes Fenster wird nur fokussiert. */
export function openEntwurfWindow(
    openWindow: (config: OpenWindowConfig) => void,
    params: { mailbox: string; draftId: string; titel?: string | null; onSent?: () => void | Promise<void> },
): string {
    const id = `mail-entwurf-${params.draftId}`;
    const W = 680;
    const H = 620;
    const x = Math.max(24, Math.round(window.innerWidth / 2 - W / 2 - 150));
    const y = Math.max(16, Math.round(window.innerHeight / 2 - H / 2 - 40));
    openWindow({
        id,
        title: params.titel ? `Entwurf: ${params.titel}` : "Entwurf",
        icon: <Mail20Regular />,
        initialSize: { width: W, height: H },
        initialPosition: { x, y },
        ohneTitelleisteAufHandy: true,
        render: () => (
            <EntwurfFenster
                windowId={id}
                mailbox={params.mailbox}
                draftId={params.draftId}
                {...(params.onSent ? { onSent: params.onSent } : {})}
            />
        ),
    });
    return id;
}
